import {useCallback, useEffect, useState} from 'react';
import type {Location} from 'react-router-dom';
import {useLocation, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';

import {appPaths} from '@/config/appPaths';
import {useAuth} from '@/hooks/useAuth';
import {authService} from '@/services/authService';
import type {AfriexRoles} from '@/types/AfriexRoles';
import {hasAdminRole, parseJwt, saveAdminSession} from '@/utils/auth';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

const useOTP = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  // `useLocation()` types `state` as `any` (React Router leaves navigation
  // state's shape entirely to the app); this is the shape `RequireAuth.tsx`'s
  // `<Navigate state={{from: location}} />` actually puts there.
  const {state} = useLocation() as {state: {from?: Location} | null};
  const [countdown, setCountdown] = useState<number | null>(null);
  const nextScreen = state?.from?.pathname || appPaths.users;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {login} = useAuth();
  const nonceData = localStorage.getItem('nonce');
  const nonce = nonceData?.toString() || '';

  const handleSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!otp) {
        throw new Error('OTP is required');
      }
      if (!nonce) {
        throw new Error('Nonce is required');
      }
      const tokenResponse = await authService.sendOTP(otp, nonce);
      const token = tokenResponse?.tokens?.admin;
      const tokenDetails = parseJwt(token);

      if (!hasAdminRole(token)) {
        // amplitudeEmitter(response.user.email,`adminPortal:logon`, "login", response.user.role, "login", "failure");
        throw new Error('Unauthorized');
      }

      // `hasAdminRole(token)` above already confirmed this token carries a
      // non-'user' role, so `tokenDetails.role` is present here even though
      // it comes from a separate `parseJwt` call the type checker can't
      // correlate with that guard.
      const adminRole = tokenDetails?.role as AfriexRoles;

      // A password reset signs in only once the new password is saved, so the
      // token goes to the set-password page in memory, not into the session.
      if (nextScreen === appPaths.setPassword) {
        await navigate(`/${appPaths.setPassword}`, {
          replace: true,
          state: {tokenResponse},
        });
        return;
      }

      // amplitudeEmitter(response.user.email,`adminPortal:logon`, "login", response.user.role, "login", "success");
      login(adminRole, saveAdminSession(tokenResponse, adminRole));
      showSuccessMessage('Verification Successful');
      setIsLoading(false);

      // `navigate` returns `void | Promise<void>` depending on router mode.
      // Awaiting it inside the existing try keeps the redirect the last step of
      // a successful verification and routes a failed navigation into the same
      // handler as the rest of this callback.
      await navigate('/' + nextScreen, {replace: true});
    } catch (error) {
      setIsLoading(false);
      showErrorMessage(
        getErrorMessage(error, 'Error Logging in: Contact Engineering'),
      );
    }
  }, [login, navigate, nextScreen, otp, nonce]);

  useEffect(() => {
    if (otp && otp.length === 6) {
      // Fire-and-forget: handleSubmit catches and toasts its own failures, so
      // this only stops an unexpected throw becoming an unhandled rejection.
      handleSubmit().catch(() => {});
    }
  }, [handleSubmit, otp]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (countdown !== null && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleResendOTP = () => {
    if (countdown === null || countdown === 0) {
      setCountdown(30);
      authService
        .resendOTP(nonce)
        .then(() => {
          toast.success('Otp resent Successfully');
        })
        .catch((err: unknown) => {
          toast.error(getErrorMessage(err, 'Error sending Otp'));
        });
    }
  };

  return {
    isLoading,
    otp,
    handleOTPChange: setOtp,
    handleResendOTP,
    countdown,
  };
};
export default useOTP;
